/* Dolmit brand tokens — extracted from dolmit.com/css/main-style.css */
/* Primary #0045CF · Navy #263457 · Text #4e5770 · Soft bgs #e6eeff #eaeffb #f7f9fd */

export interface DolmitTheme {
  blue: string;
  blueHover: string;
  blueDeep: string;
  navy: string;
  navyDeep: string;
  blueTint1: string;
  blueTint2: string;
  blueTint3: string;
  blueTint4: string;
  blueTint5: string;
  slate: string;
  slateMut: string;
  slateDim: string;
  line: string;
  lineSoft: string;
  canvas: string;
  surface: string;
  surfaceAlt: string;
  lock: string;
  g: string;
  gBg: string;
  gBd: string;
  y: string;
  yBg: string;
  yBd: string;
  r: string;
  rBg: string;
  rBd: string;
  vac: string;
  vacBg: string;
  vacBd: string;
  sh: string;
  shM: string;
  shL: string;
}

export const DOLMIT: DolmitTheme = {
  /* Brand */
  blue: "#0045CF",
  blueHover: "#0039B0",
  blueDeep: "#003AAF",
  navy: "#263457",
  navyDeep: "#1E2947",

  /* Blue tints */
  blueTint1: "#F4F7FF",
  blueTint2: "#EAEFFB",
  blueTint3: "#E6EEFF",
  blueTint4: "#D2DDFB",
  blueTint5: "#A8BDF2",

  /* Neutrals */
  slate: "#4E5770",
  slateMut: "#7A8399",
  slateDim: "#A8AEBF",
  line: "#E4E8F1",
  lineSoft: "#EEF1F7",
  canvas: "#F7F9FD",
  surface: "#FFFFFF",
  surfaceAlt: "#FBFCFE",
  lock: "#F0F2F8",

  /* Semantic */
  g: "#16865A",
  gBg: "#E8F6EE",
  gBd: "#BFE4CD",
  y: "#B17F0B",
  yBg: "#FDF6E1",
  yBd: "#F2DFA0",
  r: "#CC2F3A",
  rBg: "#FDECEE",
  rBd: "#F5C2C7",
  vac: "#7A5E2A",
  vacBg: "#FAF2E2",
  vacBd: "#E7D4A8",

  /* Elevation */
  sh: "0 1px 2px rgba(38,52,87,.05)",
  shM: "0 4px 14px rgba(38,52,87,.09)",
  shL: "0 12px 32px rgba(38,52,87,.12)",
};

export const DOLMIT_DARK: DolmitTheme = {
  blue: "#4C7DF5",
  blueHover: "#628EF8",
  blueDeep: "#89A8FA",
  navy: "#E8ECF5",
  navyDeep: "#F3F5FB",
  blueTint1: "#1B2240",
  blueTint2: "#212A4F",
  blueTint3: "#28325E",
  blueTint4: "#303D77",
  blueTint5: "#3D4E91",
  slate: "#C8CEE0",
  slateMut: "#8A92A8",
  slateDim: "#606A82",
  line: "#2A3353",
  lineSoft: "#222A46",
  canvas: "#0E1430",
  surface: "#161D3C",
  surfaceAlt: "#1A2244",
  lock: "#14193A",
  g: "#4CC185",
  gBg: "#143324",
  gBd: "#1F5739",
  y: "#E6B547",
  yBg: "#3A2D0E",
  yBd: "#5B4714",
  r: "#F17079",
  rBg: "#3A1A1F",
  rBd: "#5C2830",
  vac: "#D9B97A",
  vacBg: "#2E2514",
  vacBd: "#4F3F1F",
  sh: "0 1px 2px rgba(0,0,0,.35)",
  shM: "0 4px 14px rgba(0,0,0,.45)",
  shL: "0 12px 32px rgba(0,0,0,.55)",
};

export type DensityKey = "compact" | "cozy" | "comfortable";

export interface DensityPreset {
  row: number;
  fontBody: number;
  fontCell: number;
  avSm: number;
  avMd: number;
  avLg: number;
  badge: number;
  cellMin: number;
  hPad: string;
}

export const DENSITIES: Record<DensityKey, DensityPreset> = {
  compact: {
    row: 6,
    fontBody: 12.5,
    fontCell: 12,
    avSm: 22,
    avMd: 26,
    avLg: 28,
    badge: 10,
    cellMin: 54,
    hPad: "6px 10px",
  },
  cozy: {
    row: 8,
    fontBody: 13,
    fontCell: 12.5,
    avSm: 24,
    avMd: 28,
    avLg: 32,
    badge: 10.5,
    cellMin: 62,
    hPad: "8px 12px",
  },
  comfortable: {
    row: 11,
    fontBody: 13.5,
    fontCell: 13,
    avSm: 26,
    avMd: 30,
    avLg: 36,
    badge: 11,
    cellMin: 72,
    hPad: "10px 14px",
  },
};

// Calendar math (estimated working-hours per month) lives in
// `@shared/calendar` so the api side can reuse it for hours↔percent cell
// conversion. Re-exported here so existing browser callers keep resolving.
import { WH, isEstHoliday, workHoursForMonth } from "@shared/calendar";

export { WH, isEstHoliday, workHoursForMonth };

// `MonthEntry` deliberately does NOT carry a `wh` field anymore: that
// value depends on admin-managed extras which can change at runtime, so
// callers compute it on demand via
// `whForMonth(ym.key, extrasForYmKey(ym.key))` to stay coherent with the
// holiday cache. A precomputed module-load value would silently lie
// after the first admin CRUD.

export const TODAY = new Date();
export const CURRENT_MONTH_KEY = `${TODAY.getFullYear()}-${String(TODAY.getMonth() + 1).padStart(2, "0")}`;
export const PREV_MONTH_KEY = (() => {
  const d = new Date(TODAY.getFullYear(), TODAY.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
})();

export interface MonthEntry {
  label: string;
  short: string;
  key: string;
  year: number;
  month: number;
  isPast: boolean;
  isCurrent: boolean;
}

export const ALL_MONTHS: MonthEntry[] = (() => {
  const arr: MonthEntry[] = [];
  const names = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const start = new Date(TODAY.getFullYear(), TODAY.getMonth() - 24, 1);
  for (let i = 0; i < 48; i++) {
    const d = new Date(start.getFullYear(), start.getMonth() + i, 1);
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const name = names[m - 1] ?? "";
    arr.push({
      label: `${name} '${String(y).slice(2)}`,
      short: name,
      key: `${y}-${String(m).padStart(2, "0")}`,
      year: y,
      month: m,
      isPast:
        d.getFullYear() < TODAY.getFullYear() ||
        (d.getFullYear() === TODAY.getFullYear() && d.getMonth() < TODAY.getMonth()),
      isCurrent: d.getFullYear() === TODAY.getFullYear() && d.getMonth() === TODAY.getMonth(),
    });
  }
  return arr;
})();

export function monthsAround(focusKey: string, back: number, ahead: number): MonthEntry[] {
  const idx = ALL_MONTHS.findIndex((m) => m.key === focusKey);
  if (idx < 0) return ALL_MONTHS.slice(0, back + ahead + 1);
  const s = Math.max(0, idx - back);
  const e = Math.min(ALL_MONTHS.length, idx + ahead + 1);
  return ALL_MONTHS.slice(s, e);
}

export function monthsBetween(startKey: string, endKey: string): MonthEntry[] {
  let sI = ALL_MONTHS.findIndex((m) => m.key === startKey);
  let eI = ALL_MONTHS.findIndex((m) => m.key === endKey);
  if (sI < 0) sI = 0;
  if (eI < 0) eI = ALL_MONTHS.length - 1;
  if (sI > eI) {
    const t = sI;
    sI = eI;
    eI = t;
  }
  return ALL_MONTHS.slice(sI, eI + 1);
}

// Default visible range is the current month through +12 months — users see
// the current period forward by default; history is reachable via the picker.
export const DEFAULT_RANGE_START = CURRENT_MONTH_KEY;
export const DEFAULT_RANGE_END = (() => {
  const i = ALL_MONTHS.findIndex((m) => m.isCurrent);
  return ALL_MONTHS[Math.min(ALL_MONTHS.length - 1, i + 12)]?.key ?? CURRENT_MONTH_KEY;
})();

export const SYS_ROLES = ["admin", "lead", "accountant", "pm"] as const;
export const ROLES_CAT = [
  "Developer",
  "PM",
  "Analyst",
  "Tester",
  "Architect",
  "Designer",
  "DevOps",
] as const;

export const MONTH_NAMES_LONG = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function isoWeekOfFirst(y: number, m: number): number {
  const d = new Date(y, m - 1, 1);
  const target = new Date(d.valueOf());
  const dayNr = (d.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNr + 3);
  const firstThursday = target.valueOf();
  target.setMonth(0, 1);
  if (target.getDay() !== 4) target.setMonth(0, 1 + ((4 - target.getDay() + 7) % 7));
  return 1 + Math.ceil((firstThursday - target.valueOf()) / 604800000);
}
